<script>
  import { rowClick } from '../lib/row-link.js';
  import { onMount } from 'svelte';
  import Layout from '../components/Layout.svelte';
  import { commercialistaApi } from '../stores/api.js';
  import { activeCompanyId, setActiveCompany } from '../stores/company.js';
  import { push } from 'svelte-spa-router';
  import { t, tImmediate } from '../i18n/index.js';
  import { Briefcase, Building2, FileText, ArrowUpRight, ArrowDownLeft, Download, FileArchive } from '../lib/icons/index.js';
  import ErrorBanner from '../components/ErrorBanner.svelte';
  import StatusBadge from '../components/StatusBadge.svelte';
  import StatusLegend from '../components/StatusLegend.svelte';
  import DirectionBadge from '../components/DirectionBadge.svelte';
  import EmptyState from '../components/EmptyState.svelte';
  import { directionLabel, statusLabel } from '../lib/labels.js';

  let companies = [];
  let selectedCompanyId = '';
  let invoices = [];
  let loading = true;
  let invoicesLoading = false;
  let error = '';
  // Id of the company whose export is running, so the button can say so.
  let exportingId = '';
  // Il periodo contabile. Vuoto significa tutto l'archivio, che oltre il
  // limite del server arriva troncato: un commercialista lavora un trimestre
  // per volta, ed e quello che questa pagina deve rendere facile chiedere.
  let periodFrom = '';
  let periodTo = '';
  // Avviso non bloccante dopo un export: oggi solo il troncamento, che
  // altrimenti sarebbe indistinguibile da un archivio completo.
  let notice = '';

  /** Il trimestre o l'anno correnti, per non far digitare due date a mano. */
  function setQuickPeriod(kind) {
    const now = new Date();
    const year = now.getFullYear();
    if (kind === 'year') {
      periodFrom = `${year}-01-01`;
      periodTo = `${year}-12-31`;
      return;
    }
    const q = Math.floor(now.getMonth() / 3);
    const startMonth = q * 3;
    const endMonth = startMonth + 3;
    periodFrom = `${year}-${String(startMonth + 1).padStart(2, '0')}-01`;
    // Il giorno 0 del mese successivo e l'ultimo del mese corrente.
    periodTo = new Date(Date.UTC(year, endMonth, 0)).toISOString().slice(0, 10);
  }

  function clearPeriod() {
    periodFrom = '';
    periodTo = '';
  }

  $: periodParams = { from: periodFrom || undefined, to: periodTo || undefined };

  onMount(async () => {
    try {
      companies = await commercialistaApi.listCompanies();
    } catch (err) {
      error = err.message;
    }
    loading = false;
  });

  async function loadInvoices(companyId) {
    selectedCompanyId = companyId;
    invoicesLoading = true;
    try {
      invoices = await commercialistaApi.listInvoices({ companyId, limit: 50 });
    } catch (err) {
      error = err.message;
    }
    invoicesLoading = false;
  }

  /**
   * One CSV cell, quoted the way Excel expects.
   *
   * Anything containing the separator, a quote or a newline has to be wrapped,
   * and an embedded quote is doubled — a ragione sociale like «Rossi "Il
   * Fornaio" S.r.l.» otherwise shifts every following column by one.
   */
  function csvCell(value) {
    const s = value === null || value === undefined ? '' : String(value);
    return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }

  /** it-IT decimal comma, because that is what an Italian spreadsheet reads as a number. */
  function csvAmount(n) {
    if (n === null || n === undefined || n === '' || Number.isNaN(Number(n))) return '';
    return Number(n).toFixed(2).replace('.', ',');
  }

  function csvDate(d) {
    if (!d) return '';
    const parsed = new Date(d);
    return Number.isNaN(parsed.getTime())
      ? ''
      : parsed.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  /**
   * Export the client's invoice register as a CSV the accountant can actually
   * open. The endpoint returns JSON (one row per invoice, plus the original
   * XML), and the previous version simply dumped that JSON to a file — a
   * format no Italian accounting package imports and no one reads by hand.
   *
   * Semicolon separator and a UTF-8 BOM: Excel in an Italian locale splits on
   * ';' and needs the BOM to render the accented characters in a ragione
   * sociale correctly. The XML payload is deliberately left out — it would
   * make the file unreadable; the filename column says which document each row
   * refers to.
   */
  async function handleExport(companyId) {
    exportingId = companyId;
    error = '';
    try {
      const data = await commercialistaApi.exportInvoices(companyId, periodParams);
      const rows = data?.invoices ?? [];
      notice = data?.truncated
        ? tImmediate('commercialista.truncated', { limit: data.limit })
        : '';

      const header = [
        tImmediate('commercialista.csv.direction'),
        tImmediate('commercialista.csv.number'),
        tImmediate('commercialista.csv.date'),
        tImmediate('commercialista.csv.total'),
        tImmediate('commercialista.csv.status'),
        tImmediate('commercialista.csv.sender'),
        tImmediate('commercialista.csv.receiver'),
        tImmediate('commercialista.csv.xmlFile'),
        tImmediate('commercialista.csv.internalId'),
      ];

      const lines = [header.join(';')];
      for (const inv of rows) {
        lines.push([
          csvCell(directionLabel(inv.direction)),
          csvCell(inv.invoiceNumber),
          csvCell(csvDate(inv.invoiceDate)),
          csvCell(csvAmount(inv.totalAmount)),
          csvCell(statusLabel(inv.status)),
          csvCell(inv.senderTaxId),
          csvCell(inv.receiverTaxId),
          csvCell(inv.xmlFilename),
          csvCell(inv.id),
        ].join(';'));
      }

      // \r\n line endings and the BOM are what Excel expects from a CSV.
      const csv = `\uFEFF${lines.join('\r\n')}\r\n`;
      const company = companies.find(c => c.id === companyId);
      const slug = (company?.ragioneSociale || companyId.slice(0, 8))
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '').toLowerCase();

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fatture_${slug || 'export'}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      error = err.message;
    } finally {
      exportingId = '';
    }
  }

  /**
   * Scarica l'archivio completo del periodo: riepilogo, XML delle fatture e
   * ricevute SdI.
   *
   * Il CSV qui accanto e un elenco; questo e cio che il commercialista deve
   * effettivamente registrare. Le ricevute sono la parte che non puo
   * ricostruire da solo: la ricevuta di consegna e cio che prova che una
   * fattura e stata emessa, e una fattura scartata si considera NON emessa
   * qualunque cosa dica lo stato.
   */
  async function handleExportZip(companyId) {
    exportingId = companyId;
    error = '';
    notice = '';
    try {
      const { blob, filename } = await commercialistaApi.downloadExportZip(companyId, periodParams);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      error = err.message;
    } finally {
      exportingId = '';
    }
  }

  /**
   * Open one client's invoice.
   *
   * The detail page reads the company from the sidebar switcher, which here is
   * whichever company the commercialista last worked in — not the client whose
   * row was just clicked. Loading /companies/<other>/invoices/<id> answers 404,
   * so the context follows the click: the switcher and the page agree, and the
   * commercialista sees the client they picked.
   */
  function openInvoice(invoice) {
    if (selectedCompanyId && selectedCompanyId !== $activeCompanyId) {
      setActiveCompany(selectedCompanyId);
    }
    push(`/invoices/${invoice.id}`);
  }

  $: selectedCompany = companies.find(c => c.id === selectedCompanyId);
</script>

<Layout>
  <div class="max-w-6xl mx-auto">
    <div class="flex items-center gap-3 mb-2">
      <h1 class="section-title mb-0">{$t('commercialista.title')}</h1>
    </div>
    <p class="view-subtitle mt-2 mb-2">{$t('commercialista.intro')}</p>
    <!-- Says what the download button produces before it is pressed: an
         accountant who gets an unexpected format has to open it to find out. -->
    <p class="text-xs mb-6" style="color: var(--color-text-muted);">{$t('commercialista.exportFormatNote')}</p>

    <ErrorBanner {error} />

    {#if notice}
      <div class="card p-3 mb-4 text-xs" style="border-color: var(--color-warning); color: var(--color-text-muted);">
        {notice}
      </div>
    {/if}

    <!-- Il periodo si sceglie prima di esportare, non dopo: un commercialista
         chiede un trimestre, e senza questo la risposta era l'intero archivio. -->
    <div class="card p-4 mb-6">
      <div class="flex flex-wrap items-end gap-3">
        <div>
          <label for="period-from" class="block text-xs mb-1" style="color: var(--color-text-muted);">{$t('commercialista.periodFrom')}</label>
          <input id="period-from" type="date" bind:value={periodFrom} class="input text-sm" />
        </div>
        <div>
          <label for="period-to" class="block text-xs mb-1" style="color: var(--color-text-muted);">{$t('commercialista.periodTo')}</label>
          <input id="period-to" type="date" bind:value={periodTo} class="input text-sm" />
        </div>
        <div class="flex gap-2">
          <button type="button" class="btn-secondary text-xs" on:click={() => setQuickPeriod('quarter')}>{$t('commercialista.periodQuarter')}</button>
          <button type="button" class="btn-secondary text-xs" on:click={() => setQuickPeriod('year')}>{$t('commercialista.periodYear')}</button>
          <button type="button" class="btn-secondary text-xs" on:click={clearPeriod} disabled={!periodFrom && !periodTo}>{$t('commercialista.periodAll')}</button>
        </div>
      </div>
      <p class="text-xs mt-3" style="color: var(--color-text-muted);">
        {periodFrom || periodTo
          ? $t('commercialista.periodActive', { from: periodFrom || '…', to: periodTo || '…' })
          : $t('commercialista.periodNone')}
      </p>
    </div>

    {#if loading}
      <div class="text-center py-8" style="color: var(--color-text-muted);">{$t('app.loading')}</div>
    {:else if companies.length === 0}
      <EmptyState title={$t('commercialista.empty')} body={$t('commercialista.emptyHint')} icon={Briefcase} />
    {:else}
      <!-- Company cards grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {#each companies as company}
          <!-- The whole card selects the company for a pointer; the keyboard path
               is the company name, a real button, so the export buttons beside
               it are not nested inside another control. -->
          <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
          <div class="card p-4 cursor-pointer transition-all"
            style={selectedCompanyId === company.id ? 'border-color: var(--color-accent);' : ''}
            on:click={() => loadInvoices(company.id)}>
            <div class="flex items-start justify-between gap-2 mb-2">
              <h3 class="min-w-0">
                <button type="button" class="inline-flex items-center gap-2 text-left text-sm cell-strong hover:underline"
                  aria-pressed={selectedCompanyId === company.id}
                  on:click|stopPropagation={() => loadInvoices(company.id)}>
                  <Building2 size={16} class="text-accent shrink-0" aria-hidden="true" />
                  {company.ragioneSociale}
                </button>
              </h3>
              <div class="flex items-center gap-1">
                <button on:click|stopPropagation={() => handleExport(company.id)}
                  disabled={exportingId === company.id}
                  class="icon-button disabled:opacity-50"
                  aria-label={$t('commercialista.exportLabel', { name: company.ragioneSociale })}
                  title={$t('commercialista.exportHint')}>
                  <Download size={14} class={exportingId === company.id ? 'animate-pulse' : ''} />
                </button>
                <!-- L'archivio, non l'elenco: XML delle fatture e ricevute SdI. -->
                <button on:click|stopPropagation={() => handleExportZip(company.id)}
                  disabled={exportingId === company.id}
                  class="icon-button disabled:opacity-50"
                  aria-label={$t('commercialista.exportZipLabel', { name: company.ragioneSociale })}
                  title={$t('commercialista.exportZipHint')}>
                  <FileArchive size={14} class={exportingId === company.id ? 'animate-pulse' : ''} />
                </button>
              </div>
            </div>
            <div class="text-xs font-mono" style="color: var(--color-text-muted);">{$t('companies.fields.partitaIvaShort')}: {company.partitaIva}</div>
            <div class="flex gap-4 mt-3 text-xs">
              <div class="flex items-center gap-1" style="color: var(--color-text-muted);">
                <ArrowUpRight size={12} class="text-success" />
                <span>{$t('commercialista.outboundSent', { n: company.invoiceStats?.outbound || 0 })}</span>
              </div>
              <div class="flex items-center gap-1" style="color: var(--color-text-muted);">
                <ArrowDownLeft size={12} class="text-info" />
                <span>{$t('commercialista.inboundReceived', { n: company.invoiceStats?.inbound || 0 })}</span>
              </div>
            </div>
          </div>
        {/each}
      </div>

      <!-- Invoices for selected company -->
      {#if selectedCompanyId}
        <div class="card p-6">
          <div class="flex items-center justify-between mb-4">
            <h2 class="eyebrow eyebrow-lg" style="color: var(--color-primary);">
              {$t('commercialista.invoicesFor', { name: selectedCompany?.ragioneSociale })}
            </h2>
            <span class="text-xs font-mono" style="color: var(--color-text-muted);">{$t('commercialista.invoicesCount', { n: invoices.length })}</span>
          </div>

          {#if invoicesLoading}
            <div class="text-center py-4" style="color: var(--color-text-muted);">{$t('app.loading')}</div>
          {:else if invoices.length === 0}
            <p class="text-center py-4" style="color: var(--color-text-muted);">{$t('commercialista.noInvoices')}</p>
          {:else}
            <StatusLegend {invoices} />
            <div class="overflow-x-auto">
              <table class="table-modern table-stack w-full" role="table">
                <thead class="table-header" role="rowgroup">
                  <tr role="row">
                    <th class="table-cell table-header-cell text-left" role="columnheader">{$t('common.direction')}</th>
                    <th class="table-cell table-header-cell text-left" role="columnheader">{$t('commercialista.col.number')}</th>
                    <th class="table-cell table-header-cell text-left" role="columnheader">{$t('commercialista.col.date')}</th>
                    <th class="table-cell table-header-cell text-right" role="columnheader">{$t('commercialista.col.amount')}</th>
                    <th class="table-cell table-header-cell text-left" role="columnheader">{$t('commercialista.col.status')}</th>
                  </tr>
                </thead>
                <tbody role="rowgroup">
                  {#each invoices as invoice (invoice.id)}
                    <!-- The row opens the invoice for a pointer; the keyboard path
                         is the number, a real link that also switches the active
                         company first. -->
                    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_noninteractive_element_interactions -->
                    <tr class="table-row" role="row" on:click={(event) => rowClick(event, () => openInvoice(invoice))}>
                      <td class="table-cell" role="cell" data-label={$t('common.direction')}>
                        <DirectionBadge direction={invoice.direction} />
                      </td>
                      <td class="table-cell" role="cell" data-label={$t('commercialista.col.number')}>
                        <a href="#/invoices/{invoice.id}" class="invoice-link"
                          on:click|preventDefault={() => openInvoice(invoice)}
                          aria-label={$t('invoices.openInvoice', { numero: invoice.invoiceNumber || '-' })}>{invoice.invoiceNumber || '-'}</a>
                      </td>
                      <td class="table-cell mono text-xs text-muted" role="cell" data-label={$t('commercialista.col.date')}>{invoice.invoiceDate?.slice(0, 10) || '-'}</td>
                      <td class="table-cell mono text-right tabular" role="cell" data-label={$t('commercialista.col.amount')}>{invoice.totalAmount ? `${Number(invoice.totalAmount).toFixed(2)} €` : '-'}</td>
                      <td class="table-cell" role="cell" data-label={$t('commercialista.col.status')}>
                        <StatusBadge status={invoice.status} {invoice} />
                      </td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          {/if}
        </div>
      {/if}
    {/if}
  </div>
</Layout>
